import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import type { NicheConfig } from '../../niches/types';

interface Props {
  config: NicheConfig;
}

export default function NicheServices({ config }: Props) {
  const { services } = config;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="px-6 py-20 md:py-28 max-w-7xl mx-auto w-full">
      <div ref={ref} className="mb-12 md:mb-16 max-w-3xl">
        {services.sectionEyebrow && (
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-stroke"></div>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">
              {services.sectionEyebrow}
            </span>
          </div>
        )}
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal leading-[1.1] tracking-tight text-text-primary">
          {services.sectionTitle}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.items.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: i * 0.07, ease: 'easeOut' }}
              tabIndex={0}
              className="group relative h-[240px] rounded-2xl border border-stroke bg-surface overflow-hidden cursor-pointer transition-all duration-500 ease-out hover:bg-text-primary hover:border-text-primary hover:shadow-2xl hover:-translate-y-1 focus-within:bg-text-primary focus-within:border-text-primary focus-within:-translate-y-1"
            >
              {item.image ? (
                <>
                  {/* Grafika anime + 8bit */}
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Default - tytuł na dole nad gradientem */}
                  <div className="absolute inset-0 flex items-end p-5 bg-gradient-to-t from-bg via-bg/30 to-transparent transition-opacity duration-500 group-hover:opacity-0 group-focus-within:opacity-0">
                    <h3 className="text-lg font-semibold text-text-primary leading-snug text-balance">
                      {item.title}
                    </h3>
                  </div>
                  {/* Hover - opis na granatowym tle */}
                  <div className="absolute inset-0 flex items-center justify-center px-6 bg-text-primary/95 opacity-0 transition-opacity duration-500 group-hover:opacity-100 group-focus-within:opacity-100">
                    <p className="text-sm text-bg leading-relaxed text-center">
                      {item.description}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* First content - tylko ikona + tytuł */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 transition-all duration-500 ease-out group-hover:opacity-0 group-hover:scale-90 group-focus-within:opacity-0 group-focus-within:scale-90">
                    <div className="w-12 h-12 rounded-xl bg-bg border border-stroke flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary leading-snug text-balance">
                      {item.title}
                    </h3>
                  </div>

                  {/* Second content - description (rotate-in na hover) */}
                  <div className="absolute inset-0 flex items-center justify-center px-6 opacity-0 -rotate-90 scale-50 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:rotate-0 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:rotate-0 group-focus-within:scale-100">
                    <p className="text-sm text-bg leading-relaxed text-center">
                      {item.description}
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
